import storage from 'redux-persist/lib/storage';
import { PERSIST, persistReducer, persistStore } from 'redux-persist';
import { Action, combineReducers, configureStore } from '@reduxjs/toolkit';
import { blocksReducer } from './slices/blocks/entity/BlocksSlice';
import { authReducer } from './slices/auth/entity/AuthSlice';
import { trajectoriesReducer } from './slices/trajectories/entity/trajectorySlice';
import { bannerReducer } from './slices/banner/entity/bannerSlice';
import { debugToolbarReducer } from './slices/debugToolbar/debugToolbarSlice';
import { dashboardReducer } from './slices/dashboard/entity/DashboardSlice';
import { trajectoryToolboxReducer } from './slices/trajectoryToolbox/trajectoryToolbox';
import { filtersReducer } from './slices/filters/entity/FiltersSlice';
import { manageGroupsReducer } from './slices/groups/entity/GroupsSlice';
import { pagesApi } from './slices/page/api/pages';
import { exerciseApi } from './slices/exercises/api/exercise';
import { groupsApi } from './slices/groups/api/groups';
import { archiveApi } from './slices/archive/api/archive';
import { studentApi } from './slices/student/api/students';
import { dashboardApi } from './slices/dashboard/api/dashboard';
import { teacherApi } from './slices/teacher/api/teachers';
import { geogebraReducer } from './slices/geogebra/entity/geogebraSlice';
import { sidePanelReducer } from './slices/sidePanel/sidePanelSlice';
import { trajectorySidebarReducer } from './slices/trajectorySidebar/entity/trajectorySidebarSlice';
import { privatePageSettingsReducer } from './slices/privatePageSettings/entity/privatePageSettingsSlice';
import { fullscreenModeReducer } from './slices/screenMode/entity/fullScreenModeSlice';
import { solutionsModeReducer } from './slices/solutions/entity/solutionsModeSlice';
import { teacherViewModeReducer } from './slices/teacher/entity/teacherViewModeSlice';
import { notesApi } from './slices/notes/api/notes';
import { blockNotesReducer } from './slices/notes/entity/BlockNotesSlice';
import { scoreApi } from './slices/score-progress/score';
import { progressApi } from './slices/score-progress/progress';
import { manualPaperDigitalApi } from './slices/manuals/api/manualPaperDigitalApi';
import { authApi } from './slices/auth/entity/api/auth';
import { studyCardApi } from './slices/study-card/api/studyCard';
import { bookmarkApi } from './slices/bookmark/api/bookmarkApi';
import { bookmarkReducer } from './slices/bookmark/entity/bookmarkEntity';
import { hubspotApi } from './slices/hubspot/api';
import { scoringNotificationReducer } from './slices/score-progress/notification';
import { ctasSliceReducer } from './slices/ctas/CtasSlice';
import { froalaApi } from './slices/froala/entity';
import { trajectoryPrintPdfSliceReducer } from './slices/print/slice';
import { chapterSliceReducer } from './slices/chapter/ChapterSlice';
import { chaptersReducer } from './slices/chapter/chaptersSlice';
import { navigationBarReducer } from './slices/navigationBar/navigationBarSlice';
import { studyCardReducer } from './slices/study-card/entity/StudyCardSlice';
import { printPdfApi } from './slices/print/entity';
import { booksReducer } from './slices/book/bookEntity';
import { presentationModeReducer } from './slices/presentationMode/presentationModeSlice';
import { impersonateApi } from './slices/impersonate/impersonateApi';
import { notificationsApi } from './slices/comunications/notifications/notificationsApi';
import { notificationsSliceReducer } from './slices/comunications/notifications/notificationsSlice';
import { surveyApi } from './slices/survey/surveyApi';
import { tableOfContentsApi } from 'src/store/table-of-contents/tableOfContentsApi';
import { stepperApi } from 'src/store/stepper/stepperApi';
import { tableOfContentsReducer } from 'src/store/table-of-contents/tableOfContentsSlice';
import { PlataformGuideApi } from 'src/store/plataform-guide/PlataformGuideApi';
import { searchContentApi } from 'src/store/search-content/searchContentApi';

const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['bookmark'],
  whitelist: [
    'studyCard',
    'scoringNotification',
    'ctasSlice',
    'chapters',
    'navigationBar',
    'auth',
    'books',
    'tableOfContents',
  ],
};

const combinedReducer = combineReducers({
  auth: authReducer,
  blocks: blocksReducer,
  books: booksReducer,
  chapters: chaptersReducer,
  trajectories: trajectoriesReducer,
  banner: bannerReducer,
  debugToolbar: debugToolbarReducer,
  trajectoryToolbox: trajectoryToolboxReducer,
  dashboard: dashboardReducer,
  filters: filtersReducer,
  studyCard: studyCardReducer,
  manageGroups: manageGroupsReducer,
  geogebra: geogebraReducer,
  sidepanel: sidePanelReducer,
  trajectorySidebar: trajectorySidebarReducer,
  privatePageSettings: privatePageSettingsReducer,
  fullscreenMode: fullscreenModeReducer,
  solutionsMode: solutionsModeReducer,
  teacherViewMode: teacherViewModeReducer,
  blockNotes: blockNotesReducer,
  bookmark: bookmarkReducer,
  scoringNotification: scoringNotificationReducer,
  ctasSlice: ctasSliceReducer,
  trajectoryPrintPdfSlice: trajectoryPrintPdfSliceReducer,
  presentationMode: presentationModeReducer,
  chapterSlice: chapterSliceReducer,
  navigationBar: navigationBarReducer,
  notifications: notificationsSliceReducer,
  tableOfContents: tableOfContentsReducer,
  [pagesApi.reducerPath]: pagesApi.reducer,
  [groupsApi.reducerPath]: groupsApi.reducer,
  [exerciseApi.reducerPath]: exerciseApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [archiveApi.reducerPath]: archiveApi.reducer,
  [studentApi.reducerPath]: studentApi.reducer,
  [teacherApi.reducerPath]: teacherApi.reducer,
  [notesApi.reducerPath]: notesApi.reducer,
  [scoreApi.reducerPath]: scoreApi.reducer,
  [progressApi.reducerPath]: progressApi.reducer,
  [manualPaperDigitalApi.reducerPath]: manualPaperDigitalApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [studyCardApi.reducerPath]: studyCardApi.reducer,
  [bookmarkApi.reducerPath]: bookmarkApi.reducer,
  [hubspotApi.reducerPath]: hubspotApi.reducer,
  [froalaApi.reducerPath]: froalaApi.reducer,
  [printPdfApi.reducerPath]: printPdfApi.reducer,
  [impersonateApi.reducerPath]: impersonateApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [surveyApi.reducerPath]: surveyApi.reducer,
  [tableOfContentsApi.reducerPath]: tableOfContentsApi.reducer,
  [stepperApi.reducerPath]: stepperApi.reducer,
  [PlataformGuideApi.reducerPath]: PlataformGuideApi.reducer,
  [searchContentApi.reducerPath]: searchContentApi.reducer,
});

const rootReducer = (state: ReturnType<typeof combinedReducer> | undefined, action: Action) => {
  if (action.type === 'logout/logOff') {
    state = undefined;
  }
  return combinedReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const shouldShowDevTools = process.env.REACT_APP_ENVIRONMENT === 'local';

export const store = configureStore({
  reducer: persistedReducer,
  devTools: shouldShowDevTools,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: { ignoredActions: [PERSIST] },
    }).concat(
      exerciseApi.middleware,
      pagesApi.middleware,
      groupsApi.middleware,
      dashboardApi.middleware,
      archiveApi.middleware,
      studentApi.middleware,
      teacherApi.middleware,
      notesApi.middleware,
      scoreApi.middleware,
      progressApi.middleware,
      manualPaperDigitalApi.middleware,
      authApi.middleware,
      studyCardApi.middleware,
      bookmarkApi.middleware,
      hubspotApi.middleware,
      froalaApi.middleware,
      printPdfApi.middleware,
      impersonateApi.middleware,
      notificationsApi.middleware,
      surveyApi.middleware,
      tableOfContentsApi.middleware,
      stepperApi.middleware,
      PlataformGuideApi.middleware,
      searchContentApi.middleware
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
